/**
 * Every visible string of the three pay steps (#1036): `/plan`, `/plan/return`
 * and `/plan/failed`. The words come from the approved boards
 * (`ChoosePlan.dc.html`, `PaymentReturn.dc.html`, `PaymentFailed.dc.html`,
 * round 2 copy: talking is included, there is no top-up, no trial); the states
 * the boards do not draw are worded here once and nowhere else.
 *
 * A token written `[LIKE THIS]` is a placeholder the product owner has not
 * filled yet. `splitTokens` finds them so a page can draw each in a visible
 * `PlaceholderToken`; nothing here ever invents the figure behind one. A price
 * is never written in this file: it is read from `GET /api/billing/plans`, and
 * `[PRICE]` is drawn only when that answers 503 `billing_unavailable`.
 */

export const TOKEN = {
  PLAN: "[PLAN]",
  PRICE: "[PRICE]",
  CURRENCY: "[CURRENCY]",
  TAX_WORDING: "[TAX WORDING]",
  NEXT_PAYMENT_DATE: "[NEXT PAYMENT DATE]",
  PLAYER_NAME: "[PLAYER'S FULL NAME]",
  GUARDIAN_EMAIL: "[GUARDIAN'S EMAIL]",
  EMAIL_ADDRESS: "[EMAIL ADDRESS]",
};

const TOKEN_RE = /(\[[A-Z][A-Z' ]*\])/g;

/** "Prices are in [CURRENCY]." -> ["Prices are in ", "[CURRENCY]", "."]; tokens are the odd entries. */
export function splitTokens(text) {
  return String(text).split(TOKEN_RE).filter((part) => part !== "");
}
export const isToken = (part) => /^\[[A-Z][A-Z' ]*\]$/.test(part);

// ---- the shared flow row ----
export const FLOW_CHOOSE = ["Account made", "Choose a plan", "Pay", "Set up"];
export const FLOW_RETURN = ["Account made", "Plan chosen", "Paid", "Set up"];
export const FLOW_FAILED = ["Account made", "Plan chosen", "Payment not finished"];
/** Which entry of the flow row is the current one. */
export const FLOW_CURRENT = { choose: 1, return: 2, failed: 2 };

// ---- ChoosePlan ----
export const CHOOSE_TITLE = "Choose your plan";
export const CHOOSE_LEDE_BOTH =
  "You ticked Player and Coach, so the plan for both is the one you are choosing.";
export const CHOOSE_LEDE_PLAYER = "You ticked Player, so the player plan is the one you are choosing.";
export const CHOOSE_LEDE_COACH = "You ticked Coach, so the coach plan is the one you are choosing.";
export const PAY_LABEL = "Pay";
export const PERIOD_MONTHLY = "Monthly";
export const PERIOD_ANNUAL = "Annual";
export const TEN_PERCENT_OFF = "10% off";
export const PER_MONTH = "a month";
export const PER_YEAR = "a year · 10% off";

export const CARD_FEE_LINE = (fee) => `+ ${fee} card fee`;
export const TOTAL_MONTHLY = (total) => `${total} a month`;
export const TOTAL_ANNUAL = (total) => `${total} a year`;

export const PLAN_NAME = {
  player: "Player only",
  coach: "Coach only",
  both: "Player and coach",
};
export const PLAN_NOTE_BOTH = "Your own record, and the players linked to you. One account, both surfaces.";
export const YOUR_PLAN = "Your plan";
export const BOTH_ROLES_LINE = "One plan covers both your roles.";
export const pricesIn = (currencyText) => `Prices are in ${currencyText}.`;

export const CHARGES_LABEL = "What you are charged";
export const CHARGES_LINES = [
  "The first charge is today, the day you make your account and pay.",
  "It renews on this date, each month or each year on the annual plan, until you stop it.",
  "You can stop billing in Settings. There is no trial.",
];
export const GUARDIAN_HOLDS_CARD = "For a player under 18, the parent or guardian holds the card.";
export const NO_REFUND_ANNUAL =
  "Stopping an annual plan stops the next yearly payment. The year already paid is not refunded, and the plan runs to its end.";

export const STRIPE_LABEL = "What Stripe holds, and what this app keeps";
export const STRIPE_HOLDS = "Stripe holds your card. It is taken on Stripe's own page, and this app never sees it or stores it.";
export const APP_KEEPS = "This app keeps your plan, and whether it is paid. Nothing else about the payment.";
export const RECEIPTS_LINE = "Receipts are Stripe's emails, sent to the address on the payment.";
export const STATEMENT_DESCRIPTOR = "CRICKETCOMPASS";
export const merchantLine = () => `You come back here when it is done. The charge shows on your statement as ${STATEMENT_DESCRIPTOR}.`;

export const GUARDIAN_LABEL = "Parent or guardian's email";
export const GUARDIAN_HELP = `${TOKEN.EMAIL_ADDRESS} The parent or guardian pays. Stripe's receipts go to this address.`;
export const GUARDIAN_REQUIRED = "Enter the parent or guardian's email to continue.";
export const GUARDIAN_INVALID = "That does not look like an email address.";
export const DOB_LOCK = "The date of birth is locked after the first payment.";

export const CONTINUE_LABEL = "Continue to payment";
export const OPENING_STRIPE = "Opening Stripe";

export const PLANS_FAILED_WHAT = "the plans";
export const RETRY = "Try again";
export const CHECKOUT_COULD_NOT_OPEN =
  "Stripe's page did not open. Nothing has been charged. Try again in a moment.";
export const ALREADY_SUBSCRIBED = "You already have a plan. It is in Settings.";
export const DOB_NEEDED = "Add your date of birth in Settings first, so we know who pays.";
export const OPEN_SETTINGS = "Open Settings";

// ---- PaymentReturn ----
export const RETURN_EYEBROW = "Back from Stripe";
export const RETURN_TITLE_PAID = "That's sorted";
export const RETURN_LEDE_PAID = "Your first payment has gone through. Here is what you are on, and what happens next.";
export const RETURN_TITLE_CONFIRMING = "Checking with Stripe";
export const RETURN_LEDE_CONFIRMING = "Stripe's message has not reached us yet. This takes a few seconds. Please do not pay again.";
export const RETURN_TITLE_FAILED = "We could not confirm it yet";
export const RETURN_LEDE_FAILED =
  "Stripe has not told us the payment finished. Do not pay a second time: if it did go through, it will show here as soon as Stripe's message arrives.";
export const CHECK_AGAIN = "Check again";
export const CHECKING = "Checking";
export const OPEN_SETTINGS_LINK = "Check Settings";
export const RETURN_ON_LABEL = "What you are on";
export const CHARGED_TODAY = "Charged today";
export const NEXT_CHARGE = "Next charge";
export const RENEWS_NOTE_MONTHLY =
  "A month from today, and on that date each month after, until you stop it. Stop billing in Settings.";
export const RENEWS_NOTE_ANNUAL =
  "A year from today, and on that date each year after, until you stop it. Stop billing in Settings. The year already paid is not refunded.";
export const NEXT_LABEL = "What happens next";
export const NEXT_LINE = "Your record is yours from here. It stays empty until your first match; nothing in it starts at nought.";
export const GO_TO_RECORD = "Go to your record";
export const GO_TO_PLAYERS = "Go to your players";
export const RETURN_FOOT = `Your card stayed on Stripe's page; nothing about it is stored here. The charge shows on your statement as ${STATEMENT_DESCRIPTOR}. The plan and the date above are read from the subscription behind this payment, not from the address this page came back on.`;

// ---- PaymentFailed ----
export const FAILED_EYEBROW = "Back from Stripe";
export const FAILED_TITLE = "That payment didn't go through";
export const FAILED_LEDE =
  "The card was refused, or the page was closed before it finished. Nothing has been charged, and nothing has been lost.";
export const FAILED_STRIPE = "The card page was Stripe's own. No card details are stored here; the app never saw them.";
export const FAILED_ACCOUNT_LABEL = "You still have your account";
export const failedAccountLine = (name, email) => `${name} signed in, on ${email}.`;
export const FAILED_KEEPS =
  "This app keeps your plan and whether it is paid. Everything you entered is saved, you do not sign up again, and this is the same account, waiting for a payment.";
export const FAILED_UNTIL = "Until then you can reach set-up and Settings.";
export const TRY_AGAIN_LABEL = "Try the payment again";
export const RETRYING = "Opening Stripe";
export const RETRY_COULD_NOT_OPEN =
  "Stripe's page did not open. Nothing has been charged. Try again in a moment.";
export const BACK_TO_SETTINGS = "Back to Settings";
export const FAILED_FOOT =
  "A different card, or a different way to pay, is chosen on Stripe's page, not here. Stripe does not always say why a payment did not finish, so the line at the top is the common case rather than a reading of this card.";
export const GUARDIAN_NEEDS_PLAN_PAGE = "A parent or guardian's email is needed again. Choose the plan to enter it.";
export const CHOOSE_PLAN_AGAIN = "Choose the plan";

// ---- money and dates ----
/** Minor units to text in the currency Stripe named: 1000 + "nzd" -> "$10.00". Never guesses a currency. */
export function formatMoney(unitAmount, currency) {
  if (typeof unitAmount !== "number" || typeof currency !== "string" || currency === "") return null;
  try {
    return new Intl.NumberFormat("en-NZ", { style: "currency", currency: currency.toUpperCase() }).format(unitAmount / 100);
  } catch {
    return null;
  }
}

/**
 * D420: the card fee and the total (plan plus fee) of one price row, as text; null when the row holds no
 * fee, so a bare plan price is never shown as what the customer pays.
 */
export function priceWithFee(price) {
  if (!price || typeof price.fee_amount !== "number" || typeof price.unit_amount !== "number") return null;
  const fee = formatMoney(price.fee_amount, price.currency);
  const total = formatMoney(price.unit_amount + price.fee_amount, price.currency);
  return fee && total ? { fee, total } : null;
}

/** "2026-12-01T00:00:00.000Z" -> "1 December 2026" in New Zealand time; null when unreadable. */
export function formatRenewal(iso) {
  if (typeof iso !== "string" || iso === "") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("en-NZ", { day: "numeric", month: "long", year: "numeric", timeZone: "Pacific/Auckland" }).format(d);
}

/** The plan to show, from the codes the API sent: all three means this account holds both roles. */
export function planFromCodes(codes) {
  const set = new Set(codes);
  if (set.has("both")) return "both";
  if (set.has("coach") && !set.has("player")) return "coach";
  if (set.has("player")) return "player";
  return null;
}

/** The price row for one plan and period out of the `plans` array, or null. */
export function priceFor(plans, code, period) {
  return (plans || []).find((p) => p.code === code && p.period === period) || null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const looksLikeEmail = (v) => EMAIL_RE.test(String(v || "").trim());
